import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import GoogleSignInButton from './GoogleSignInButton';

const Login = () => {
  const { loading, error, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showError, setShowError] = useState(false);
  const [showInAppBrowserWarning, setShowInAppBrowserWarning] = useState(false);

  // 로그인 성공 시 리디렉션 처리
  useEffect(() => {
    if (user) {
      const searchParams = new URLSearchParams(location.search);
      const redirectUrl = searchParams.get('redirect');
      
      if (redirectUrl) {
        // 공유 링크 리디렉션
        window.location.href = decodeURIComponent(redirectUrl);
      } else {
        // 기본 대시보드로 이동
        navigate('/');
      }
    }
  }, [user, navigate, location.search]);

  // 인앱브라우저 감지
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // 인앱브라우저 감지 (보다 포괄적)
    const isInAppBrowser = (
      /FBAN|FBAV/i.test(userAgent) || // Facebook
      /Instagram/i.test(userAgent) || // Instagram
      /KAKAOTALK/i.test(userAgent) || // KakaoTalk
      /LINE/i.test(userAgent) || // LINE
      /GSA\//i.test(userAgent) || // Google Search App
      /Twitter/i.test(userAgent) || // Twitter
      /WhatsApp/i.test(userAgent) || // WhatsApp
      /Telegram/i.test(userAgent) || // Telegram
      /WeChat/i.test(userAgent) || // WeChat
      /MicroMessenger/i.test(userAgent) || // WeChat alternative
      /NaverApp/i.test(userAgent) || // Naver App
      /DaumApps/i.test(userAgent) || // Daum App
      /SamsungBrowser.*[Mm]obile/i.test(userAgent) || // Samsung Internet in app mode
      (window.navigator.standalone === true) || // iOS standalone mode
      (userAgent.includes('wv') && userAgent.includes('Android')) // Android WebView
    );

    if (isInAppBrowser) {
      setShowInAppBrowserWarning(true);
    }
  }, []);

  // 에러 메시지 표시/숨김
  const handleErrorToggle = () => {
    setShowError(!showError);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f9fa',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%'
      }}>
        {/* 로고 및 제목 */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{
            fontSize: '2.5rem',
            margin: '0 0 10px 0',
            color: '#333'
          }}>
            🔐 Password Wallet
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: '#495057',
            margin: '0 0 15px 0',
            fontWeight: '500'
          }}>
            차세대 패스워드 매니저
          </p>
          <p style={{
            fontSize: '0.95rem',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.6'
          }}>
            Web Crypto API 기반 AES-256 암호화로 <br />
            엔터프라이즈급 보안을 제공하는 스마트한 패스워드 관리 솔루션
          </p>
        </div>

        {/* 시작하기 안내 */}
        <div style={{
          marginBottom: '25px',
          padding: '18px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          border: '1px solid #bbdefb'
        }}>
          <h3 style={{ 
            margin: '0 0 8px 0', 
            color: '#1976d2',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            🚀 시작하기
          </h3>
          <p style={{
            margin: '0',
            fontSize: '0.9rem',
            color: '#1565c0',
            lineHeight: '1.5'
          }}>
            Google 계정으로 간편하게 로그인하여<br />
            모든 패스워드를 안전하게 관리하세요
          </p>
        </div>

        {/* 인앱브라우저 경고 */}
        {showInAppBrowserWarning && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#fff3e0',
            borderRadius: '8px',
            border: '1px solid #ffa000',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '10px'
            }}>
              <h4 style={{
                margin: 0,
                color: '#e65100',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                ⚠️ 인앱브라우저 감지됨
              </h4>
              <button
                onClick={() => setShowInAppBrowserWarning(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#757575',
                  padding: '0',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '0.9rem',
              color: '#424242',
              lineHeight: '1.5'
            }}>
              인앱브라우저에서는 Google 로그인이 제한될 수 있습니다.
            </p>
            <div style={{
              backgroundColor: '#ffecb3',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#5d4037'
            }}>
              <strong>🔧 해결 방법:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>브라우저 메뉴에서 "Safari에서 열기" 또는 "Chrome에서 열기" 선택</li>
                <li>아래 URL을 복사하여 기본 브라우저에 붙여넣기</li>
                <li>또는 설정에서 기본 브라우저로 열기 옵션 활성화</li>
              </ol>
            </div>
            <div style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              color: '#616161',
              wordBreak: 'break-all',
              userSelect: 'all',
              cursor: 'text'
            }}>
              {window.location.origin}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin);
                alert('URL이 복사되었습니다!');
              }}
              style={{
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: '#ff6f00',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              📋 URL 복사하기
            </button>
          </div>
        )}

        {/* 로그인 버튼 */}
        <div style={{ marginBottom: '20px' }}>
          <GoogleSignInButton disabled={loading} />
        </div>

        {/* 로딩 상태 */}
        {loading && (
          <div style={{
            padding: '15px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            color: '#1976d2',
            fontSize: '0.9rem',
            marginBottom: '20px'
          }}>
            ⏳ 로그인 처리 중...
          </div>
        )}

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            padding: '15px',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            border: '1px solid #ffcdd2',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#c62828',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                ⚠️ 로그인 오류
              </span>
              <button
                onClick={handleErrorToggle}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#c62828',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                {showError ? '숨기기' : '자세히'}
              </button>
            </div>
            
            {/* 간단한 해결방안 표시 */}
            {error.includes('브라우저') && (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                backgroundColor: '#fff3e0',
                borderRadius: '6px',
                border: '1px solid #ffcc02',
                fontSize: '0.85rem',
                color: '#e65100'
              }}>
                <strong>💡 해결방법:</strong><br />
                1. 브라우저 메뉴에서 "외부 브라우저에서 열기" 선택<br />
                2. 또는 Chrome, Safari 등에서 직접 접속<br />
                3. URL 복사 후 기본 브라우저에서 접속
              </div>
            )}
            
            {showError && (
              <div style={{
                marginTop: '10px',
                fontSize: '0.8rem',
                color: '#d32f2f',
                textAlign: 'left',
                padding: '8px',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: '4px'
              }}>
                <strong>상세 오류:</strong><br />
                {error}
              </div>
            )}
          </div>
        )}

        {/* 주요 기능 소개 */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '10px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ 
            margin: '0 0 15px 0', 
            color: '#495057',
            fontSize: '1.1rem',
            fontWeight: '600'
          }}>
            ✨ 주요 기능
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            fontSize: '0.85rem',
            color: '#6c757d',
            textAlign: 'left'
          }}>
            <div>🛡️ 실시간 보안 분석</div>
            <div>🎲 안전한 패스워드 생성</div>
            <div>🔍 중복 패스워드 감지</div>
            <div>📤 안전한 공유 기능</div>
            <div>🔐 2FA 인증 지원</div>
            <div>📱 완전 반응형 UI</div>
          </div>
        </div>

        {/* 보안 정보 */}
        <div style={{
          marginTop: '25px',
          padding: '18px',
          fontSize: '0.85rem',
          color: '#495057',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: '600', color: '#28a745' }}>
            🔒 엔터프라이즈급 보안
          </div>
          <div style={{ lineHeight: '1.5', color: '#6c757d' }}>
            • AES-256-GCM 종단간 암호화<br />
            • PBKDF2 키 파생 (100,000 iterations)<br />
            • 모든 데이터는 브라우저 네이티브 Web Crypto API로 보호
          </div>
          <div style={{ 
            marginTop: '12px', 
            fontSize: '0.75rem', 
            color: '#868e96',
            borderTop: '1px solid #dee2e6',
            paddingTop: '8px'
          }}>
            ⭐ 2025년 최신 보안 표준 적용
          </div>
        </div>

        {/* 유용한 링크들 */}
        <div style={{
          marginTop: '20px',
          textAlign: 'center'
        }}>
          {/* 보안 투명성 링크 */}
          <div style={{ marginBottom: '12px' }}>
            <a
              href="/security"
              style={{
                fontSize: '0.9rem',
                color: '#007bff',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: 'rgba(0, 123, 255, 0.05)',
                border: '1px solid rgba(0, 123, 255, 0.15)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                e.target.style.borderColor = 'rgba(0, 123, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(0, 123, 255, 0.05)';
                e.target.style.borderColor = 'rgba(0, 123, 255, 0.15)';
              }}
            >
              🛡️ 보안 투명성 확인하기
            </a>
          </div>
          
          {/* 사용자 가이드 링크 */}
          <div style={{ marginBottom: '12px' }}>
            <a
              href="/guide"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.9rem',
                color: '#28a745',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: 'rgba(40, 167, 69, 0.05)',
                border: '1px solid rgba(40, 167, 69, 0.15)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                e.target.style.borderColor = 'rgba(40, 167, 69, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(40, 167, 69, 0.05)';
                e.target.style.borderColor = 'rgba(40, 167, 69, 0.15)';
              }}
            >
              📖 사용자 가이드 보기
            </a>
          </div>
          
          <div style={{
            marginTop: '8px',
            fontSize: '0.75rem',
            color: '#6c757d',
            lineHeight: '1.4'
          }}>
            PW-Wallet의 모든 기능과 사용법을 자세히 알아보세요
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
